<template>
  <master-layout v-model="activeStep" :navigation="steps">
    <template #header>
      <div class="d-flex justify-space-between align-center pr-1">
        <p class="caption">Мастер создания пространства</p>
        <template v-if="!userRegistration">
          <e-btn
            v-if="$vuetify.display.lgAndUp"
            :to="{ name: RouteNames.SignIn }"
            append-icon-src="closeSvg"
            variant="context"
            size="M"
          >
            Закрыть
          </e-btn>
          <e-btn v-else :to="{ name: RouteNames.SignIn }" variant="context" icon size="S">
            <e-icon name="closeSvg" />
          </e-btn>
        </template>
        <e-btn v-else append-icon-src="closeSvg" variant="context" size="M" @click="logout">
          Выйти
          <template v-if="!$vuetify.display.xs">из учетной записи</template>
        </e-btn>
      </div>
    </template>

    <div>
      <e-form v-show="activeStep === 0" v-model="isLegalFormValid" ref="formRef1">
        <div class="h3 mb-4">Заполните информацию о Юридическом лице</div>
        <p class="paragraph-s_h mb-10 mb-md-12">
          Введите корректные данные в&nbsp;целях ускоренного прохождения процесса создания пространства
        </p>
        <div class="d-grid grid-col-1 row-gap-4">
          <legal-origin-select v-model="questionnaire.legal.origin" />
          <e-radio-group v-if="isRuLegal" v-model="legalType" :items="legalTypes" inline class="py-2" />
          <e-combobox
            v-model="legalData"
            :key="`inn_${legalType}`"
            :rules="innRules"
            :maska="innMaska"
            :items="legalSuggestions"
            :loading="isLoadingLegalSuggestions"
            :error-messages="innErrorText"
            item-title="data.inn"
            item-value="data.inn"
            label="ИНН"
            return-object
            no-filter
            @update:modelValue="onLegalSelect"
            @update:search="searchLegalSuggestions"
          >
            <template #item="{ item }">
              <div class="input-placeholder">{{ item.raw.data.opf.short }} {{ item.raw.data.name.full }}</div>
              <div class="paragraph-xs text-secondary">{{ item.raw.data.kpp }} {{ item.raw.data.address?.value }}</div>
            </template>
          </e-combobox>
          <e-input
            v-if="!isRuLegal || legalType === 1"
            v-model="questionnaire.legal.kpp"
            :maska="kppMaska"
            :rules="kppRules"
            :label="isRuLegal ? 'КПП' : 'КПП (не обязательно)'"
          />
          <e-input
            v-if="isRuLegal"
            v-model="questionnaire.legal.ogrn"
            :maska="ogrnipMask"
            :rules="[length(legalType === 1 ? 13 : 15)]"
            label="ОГРН / ОГРНИП"
          />
          <multilanguage-field
            v-model="questionnaire.legal.nameML"
            :rules="[required()]"
            :common-rules="[maxLength(255)]"
            label="Наименование Юридического лица"
          />
          <address-field
            v-model="questionnaire.legal.addressML"
            :rules="[required()]"
            :common-rules="[maxLength(255)]"
            label="Юридический адрес"
          />
          <e-checkbox
            v-if="isRuLegal"
            v-model="questionnaire.legal.isNotTaxPayer"
            label="Не является плательщиком НДС"
            class="mt-2"
          />
        </div>
      </e-form>
      <e-form v-show="activeStep === 1" v-model="isObjectFormValid" ref="formRef2">
        <div class="h3 mb-4">Заполните информацию об объекте размещения</div>
        <p class="paragraph-s_h mb-9">
          Укажите, пожалуйста, наименование отеля по&nbsp;которому гости могут вас легко найти и&nbsp;идентифицировать
        </p>
        <div class="d-grid grid-col-1 row-gap-4">
          <multilanguage-field
            v-model="questionnaire.object.nameML"
            :rules="[required()]"
            :common-rules="[maxLength(255)]"
            label="Наименование"
          />
          <e-select
            v-model="questionnaire.object.type"
            :items="hotelTypeSuggestions"
            :rules="[required()]"
            item-title="name"
            return-object
            label="Тип"
          />
          <address-field
            v-model="questionnaire.object.addressML"
            :rules="[required()]"
            :common-rules="[maxLength(255)]"
            label="Адрес"
          />
        </div>
      </e-form>
      <e-form v-show="activeStep === 2" v-model="isContactsFormValid" ref="formRef3">
        <div class="h3 mb-4">Контактные данные объекта размещения</div>
        <p class="paragraph-s_h mb-9">
          Укажите, пожалуйста, рабочие контактные данные по&nbsp;которым можно с&nbsp;вами связаться для дальнейшего
          взаимодействия
        </p>
        <div class="d-grid grid-col-1 row-gap-4">
          <phone-field v-model="questionnaire.contact.phone" :rules="[required()]" />
          <e-input v-model="questionnaire.contact.email" :rules="[required(), emailRule()]" label="E-mail" />
          <e-input v-model="questionnaire.contact.webSite" :rules="[URLRule()]" label="Адрес сайта (опционально)" />
        </div>
      </e-form>
      <template v-if="activeStep === 3">
        <div class="h3 mb-4">Благодарим за регистрацию</div>
        <p class="paragraph-s_h mb-10">
          Скоро с вами свяжется наш менеджер для подтверждения регистрации, пожалуйста ожидайте
        </p>
      </template>

      <div :class="{ 'mt-auto': activeStep < 3 }">
        <template v-if="activeStep === 3">
          <e-alert v-if="!hasChanges">
            <div>
              <div class="paragraph-m">
                <b>Заметили неточности в отправленной анкете?</b>
              </div>
              <p class="paragraph-s_h text-secondary mt-2">
                До завершения процесса валидации вы можете отредактировать введённые данные и отправить анкету повторно.
              </p>
            </div>
          </e-alert>
          <e-alert v-else variant="error">
            <div>
              <div class="paragraph-m">
                <b>Вы внесли правки в анкету, но не отправили её на проверку.</b>
              </div>
              <p class="paragraph-s_h text-secondary mt-2">
                Отправьте анкету сейчас, пока наши менеджеры не взяли её в работу.
              </p>
            </div>
          </e-alert>
        </template>

        <div class="d-flex justify-space-between mt-10">
          <template v-if="activeStep < 3">
            <e-btn
              v-if="activeStep > 0"
              prepend-icon-src="arrowLeft"
              :icon="shortBtnContent"
              variant="secondary"
              class="mr-3"
              @click="back"
            >
              <template v-if="!shortBtnContent"> Назад</template>
            </e-btn>
            <v-spacer v-if="!userRegistration" />
            <e-btn
              v-if="activeStep < 2"
              append-icon-src="arrowRight"
              :variant="!userRegistration ? 'primary' : 'secondary'"
              :icon="shortBtnContent"
              :loading="isNextLoading && !isLoading"
              @click="nextStep"
            >
              <template v-if="!shortBtnContent"> Далее</template>
            </e-btn>
            <v-spacer v-if="userRegistration" />
          </template>
          <e-btn
            v-if="isSaveButtonVisible"
            :loading="isLoading"
            :disabled="!hasChanges"
            variant="primary"
            @click="sendQuestionnaire"
          >
            Отправить
            <template v-if="!$vuetify.display.xs"> на проверку</template>
          </e-btn>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { computed, ref, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { LegalSuggestion, LegalSuggestion as LegalSuggestionType } from '@/core/types/suggestions';
import type { IsLegalFreeQuery } from '@/graphql/generated';
import type { MultilanguageValue } from '@/core/utils/languages';
import LegalOriginSelect from '@/core/components/common/LegalOriginSelect.vue';
import PhoneField from '@/core/components/fields/PhoneField.vue';
import AddressField from '@/core/components/fields/AddressField.vue';
import { EAlert, EBtn, ECheckbox, ECombobox, EForm, EIcon, EInput, ERadioGroup, ESelect, MultilanguageField } from '@/core/components/ui';
import { useServices } from '@/core/composables/services';
import { useSuggestions } from '@/core/composables/suggestions';
import {
  innOtherMask,
  innRuIPMask,
  kppOtherMask,
  kppRuMask,
  ogrnipMask,
} from '@/core/consts/masks';
import MasterLayout from '@/core/layouts/components/master/MasterLayout.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { deepCloneObject, deepEquals } from '@/core/utils/object';
import { clearPhoneFormat } from '@/core/utils/string';
import { isString } from '@/core/utils/type-guards';
import { emailRule, length, maxLength, minLength, required, URLRule } from '@/core/utils/validation';
import { LegalOrigin } from '@/graphql/generated';
import { IsLegalFree, SendSupplierQuestionnaire, UpdateSupplierQuestionnaire } from '@/parts/auth/gql/profile.gql';
import { useAuthStore } from '@/parts/auth/store/auth';
import { getDefaultMultilanguageValues } from '@/core/utils/languages';

const { $api, $auth } = useServices();
const $route = useRoute();
const $display = useDisplay();
const { profile, getProfile } = useAuthStore();

const logout = () => {
  void $auth.logout();
};

const userRegistration = toRef(() => profile.value?.userRegistration?.questionnaire);

const legalType = ref(userRegistration.value?.legal?.details?.tin.length === 12 ? 2 : 1);

const legalTypes = [
  {
    title: 'Юридическое лицо',
    value: 1,
  },
  {
    title: 'Индивидуальный предприниматель',
    value: 2,
  },
];

const questionnaire = ref({
  legal: {
    origin: userRegistration.value?.legalOrigin || LegalOrigin.Ru,
    nameML: userRegistration.value?.legal?.nameML?.length
      ? userRegistration.value.legal.nameML
      : getDefaultMultilanguageValues(),
    inn: userRegistration.value?.legal?.details?.tin || null,
    kpp: userRegistration.value?.legal?.details?.kpp || '',
    ogrn: userRegistration.value?.legal?.details?.ogrn || null,
    addressML: userRegistration.value?.legal?.addressML?.length
      ? userRegistration.value.legal.addressML
      : getDefaultMultilanguageValues(),
    isNotTaxPayer: userRegistration.value?.legal?.isNotTaxPayer || false,
  },
  object: {
    nameML: userRegistration.value?.accommodationObject?.nameML?.length
      ? userRegistration.value.accommodationObject.nameML
      : getDefaultMultilanguageValues(),
    type: userRegistration.value?.accommodationObject?.type
      ? {
          cid: userRegistration.value?.accommodationObject?.type.cid,
          name: userRegistration.value?.accommodationObject?.type.name,
        }
      : null,
    addressML: userRegistration.value?.accommodationObject?.addressData?.addressML?.length
      ? userRegistration.value.accommodationObject.addressData.addressML
      : getDefaultMultilanguageValues(),
  },
  contact: {
    phone: userRegistration.value?.contact?.phone || '',
    email: userRegistration.value?.contact?.email || '',
    webSite: userRegistration.value?.contact?.webSite || '',
  },
});

const isRuLegal = computed(() => questionnaire.value.legal.origin === LegalOrigin.Ru);

const defaultQuestionnaire = ref(deepCloneObject(questionnaire.value));

const hasChanges = toRef(() => !deepEquals(defaultQuestionnaire.value, questionnaire.value));

const legalData = ref(
  userRegistration.value?.legal?.details
    ? {
        data: {
          inn: userRegistration.value?.legal?.details.tin,
          kpp: userRegistration.value?.legal?.details.kpp,
        },
      }
    : undefined,
);

const isSaveButtonVisible = computed(
  () =>
    activeStep.value === 2 ||
    (userRegistration.value && [0, 1, 2].includes(activeStep.value)) ||
    (hasChanges.value && activeStep.value === 3),
);

const innMaska = computed(() => {
  if (isRuLegal.value) {
    return innRuIPMask; 
  }

  return innOtherMask;
});

const innLength = computed(() => (legalType.value === 1 ? 10 : 12));
const innMinLength = 8;

const innRules = computed(() => {
  if (isRuLegal.value) {
    return [required(), length(innLength.value, undefined, 'data.inn')];
  }

  return [required(), minLength(innMinLength)];
});

const kppMaska = computed(() => (isRuLegal.value ? kppRuMask : kppOtherMask));

const kppRules = computed(() => {
  if (isRuLegal.value) {
    return legalType.value === 1 ? [required(), length(9)] : [];
  }

  return [];
});

const isLegalFormValid = ref(false);
const isObjectFormValid = ref(false);
const isContactsFormValid = ref(false);
const activeStep = ref(0);
const isLoading = ref(false);
const formRef1 = ref();
const formRef2 = ref();
const formRef3 = ref();

watch(legalType, (newType, oldType) => {
  if (newType !== oldType) {
    questionnaire.value.legal.kpp = '';
    legalSuggestions.value = [];
  }
});

const setActiveStep = (index: number) => {
  activeStep.value = index;
};

if (userRegistration.value) {
  setActiveStep(3);
}

const isNextLoading = ref(false);

const innErrorText = ref('');
const isLegalCorrect = ref(false);

const checkLegal = async () => {
  await formRef1.value?.validate();

  if (!isLegalFormValid.value) {
    return false;
  }

  isNextLoading.value = true;
  innErrorText.value = '';

  const result = await $api.query<IsLegalFreeQuery>(IsLegalFree, {
    tin: questionnaire.value.legal.inn,
  });

  isNextLoading.value = false;

  if (result?.errors) {
    return false;
  }

  if (!result?.data?.isLegalFree) {
    innErrorText.value = 'Уже существует аккаунт с данным Юридическим лицом';
    return false;
  } else {
    isLegalCorrect.value = true;
  }

  return true;
};

const nextStep = async () => {
  if (activeStep.value === 0) {
    const result = await checkLegal();

    if (!result) {
      return;
    }
  }

  if (activeStep.value === 0) {
    await formRef1.value?.validate();
  } else if (activeStep.value === 1) {
    await formRef2.value?.validate();
  } else if (activeStep.value === 2) {
    await formRef3.value?.validate();
  }

  if ((!isLegalFormValid.value && activeStep.value === 0) || (!isObjectFormValid.value && activeStep.value === 1)) {
    return;
  }

  activeStep.value++;
};

const filterFilledML = (mlValues: MultilanguageValue[]) => {
  return mlValues.filter(item => item.value && item.value.trim() !== '');
};

const sendQuestionnaire = async () => {
  isLoading.value = true;
  await checkLegal();
  isLoading.value = false;

  await formRef1.value?.validate();
  await formRef2.value?.validate();
  await formRef3.value?.validate();

  if (!isLegalFormValid.value) {
    setActiveStep(0);
    return;
  }
  if (!isObjectFormValid.value) {
    setActiveStep(1);
    return;
  }
  if (!isContactsFormValid.value) {
    setActiveStep(2);
    return;
  }

  isLoading.value = true;

  let kpp = questionnaire.value.legal.kpp || null;

  if (isRuLegal.value && legalType.value !== 1) {
    kpp = null;
  }
  
  const isNotTaxPayer = isRuLegal.value ? questionnaire.value.legal.isNotTaxPayer : true;

  const legalNameML = filterFilledML(questionnaire.value.legal.nameML);
  const legalAddressML = filterFilledML(questionnaire.value.legal.addressML);
  const objectNameML = filterFilledML(questionnaire.value.object.nameML);
  const objectAddressML = filterFilledML(questionnaire.value.object.addressML);

  const payload = {
    legalNameML: legalNameML.length ? legalNameML : null,
    legalAddressML: legalAddressML.length ? legalAddressML : null,
    objectNameML: objectNameML.length ? objectNameML : null,
    objectAddressML: objectAddressML.length ? objectAddressML : null,
    tin: questionnaire.value.legal.inn,
    kpp,
    ogrn: isRuLegal.value ? questionnaire.value.legal.ogrn : null,
    legalTownName: legalAddressML.find(item => item.value)?.value?.split(',')[0] || '',
    isNotTaxPayer,
    legalOrigin: questionnaire.value.legal.origin,
    objectTypeName: questionnaire.value.object.type!.name,
    objectTypeCid: questionnaire.value.object.type!.cid,
    objectTownName: objectAddressML.find(item => item.value)?.value?.split(',')[0] || '',
    phone: clearPhoneFormat(questionnaire.value.contact.phone),
    email: questionnaire.value.contact.email,
    webSite: questionnaire.value.contact.webSite || null,
    version: 1,
    isNew: !!$route.query.new,
  };

  let result;

  if (userRegistration.value) {
    result = await $api.mutation(UpdateSupplierQuestionnaire, payload);
  } else {
    result = await $api.mutation(SendSupplierQuestionnaire, payload);
  }

  if (result?.errors) {
    isLoading.value = false;
    return;
  }

  await getProfile(true);

  defaultQuestionnaire.value = deepCloneObject(questionnaire.value);

  isLoading.value = false;

  setActiveStep(3);
};

const back = () => {
  activeStep.value--;
};

const { getDadataAddressSuggestions, getLegalSuggestions, useHotelTypeSuggestions } = useSuggestions();
const isSearchLoading = ref(false);
const addressSuggestions = ref<
  {
    address: string;
    townName: string;
  }[]
>([]);

const { suggestions: hotelTypeSuggestions } = useHotelTypeSuggestions();

const searchAddresses = useDebounceFn(async (searchString: string) => {
  isSearchLoading.value = true;

  const result = await getDadataAddressSuggestions(searchString);

  addressSuggestions.value = result.map((item) => ({
    address: item.value,
    townName: item.data.city,
  }));

  isSearchLoading.value = false;
}, 500);

const isLoadingLegalSuggestions = ref(false);
const legalSuggestions = ref<LegalSuggestionType[]>([]);

const onLegalSelect = (value: LegalSuggestion | string | null) => {
  innErrorText.value = '';

  if (!value) {
    questionnaire.value.legal.inn = null;
  } else if (isString(value)) {
    questionnaire.value.legal.inn = value;
  } else {
    questionnaire.value.legal.inn = value.data.inn;
    questionnaire.value.legal.kpp = value.data.kpp;
    questionnaire.value.legal.ogrn = value.data.ogrn;
    
    const ruName = questionnaire.value.legal.nameML.find(item => item.langCode === 'RU');
    if (ruName) {
      ruName.value = value.data.name.full;
    }
    
    const ruAddress = questionnaire.value.legal.addressML.find(item => item.langCode === 'RU');
    if (ruAddress && value.data.address?.value) {
      ruAddress.value = value.data.address.value;
    }
  }
};

const searchLegalSuggestions = useDebounceFn(async (searchString: string) => {
  if (!searchString || searchString.length < 3) {
    legalSuggestions.value = [];
    return;
  }
  
  isLoadingLegalSuggestions.value = true;

  const queryType = !isRuLegal.value
  ? undefined
  : legalType.value === 1 ? 'LEGAL' : 'INDIVIDUAL';

  legalSuggestions.value = await getLegalSuggestions(searchString, queryType);

  isLoadingLegalSuggestions.value = false;
}, 500);

const shortBtnContent = toRef(() => !!userRegistration.value && $display.xs.value);

const steps = toRef(() => [
  {
    title: 'Юридическое лицо',
    disabled: false,
    error: !!(!isLegalFormValid.value && userRegistration.value),
  },
  {
    title: 'Информация об объекте размещения',
    disabled: !isLegalCorrect.value && !userRegistration.value,
    error: !!(!isObjectFormValid.value && userRegistration.value),
  },
  {
    title: 'Контактные данные',
    disabled: !isObjectFormValid.value && !userRegistration.value,
    error: !!(!isContactsFormValid.value && userRegistration.value),
  },
  {
    title: 'Валидация',
    disabled: !userRegistration.value,
  },
]);
</script>
